import { useState, useEffect, useContext } from 'react';
import Location from "../../../hooks/Location";
import { useFirestoreID, useFirestoreGeneral, useFirestoreGeneralTwo, useFirestoreGeneralOrderBy } from "../../../firebase/useFirestore";
import useSettings from "../../../hooks/Settings";
import { Auth } from "../../../StateManagment/Auth";
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import Tooltip from "../../common/Tooltip";
import { useHistory } from "react-router-dom";
import { client } from "../../../hooks/Client";
import FieldResponsesCountPersonas from "../FieldResponsesCountPersonas";
import MomentMeta from "../../Research/MomentMeta";
import firebase from '../../../firebase/compat';
import EffectMeta from "../../effects/EffectMeta";
import Table from "../../common/table/Table.jsx";
import TableCell from "../../common/table/TableCell.jsx";
import SelectableGrid from "../../common/SelectableGrid";
import resolveRespondentTitle from "../../../helpers/questionnaires/resolveRespondentTitle";

const ReserachResultsPersonas = () => {
    // Context
    const [auth] = useContext(Auth);

    // State
    const [questionnaireID, setQuestionnaireID] = useState('');
    const [researchTitle, setResearchTitle] = useState('');
    const [title, setTitle] = useState('');
    const [personaTitle, setPersonaTitle] = useState('');
    const [resultType, setResultType] = useState('forms');
    const [forms, setForms] = useState([]);

    // Hooks and settings
    const researchId = Location()[4];
    const personaId = Location()[3];
    const history = useHistory();
    const options = { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' };

    // Firestore queries
    const research = useFirestoreID('Research', researchId ? researchId : '');
    const fields = useFirestoreGeneralOrderBy('QuestionnaireFields', 'QuestionnaireID', questionnaireID ? questionnaireID : '', 'Position', 'asc');
    const personas = useFirestoreID('Personas', personaId ? personaId : '');
    // Een vragenlijst kan ook aan een huishouden gekoppeld zijn; dat ID staat in
    // hetzelfde 'Persona'-veld, dus de kop moet ook een adres kunnen oplossen.
    const adresses = useFirestoreID('Adresses', personaId ? personaId : '');
    const personaDataRows = useFirestoreGeneral('PersonaData', 'PersonaID', personaId ? personaId : '');
    const results = useFirestoreGeneralTwo('QuestionnairesResponses', 'ResearchID', researchId ? researchId : '', 'Persona', personaId ? personaId : '');

    // Set research title and questionnaire ID
    useEffect(() => {
        research && research.forEach(item => {
            setQuestionnaireID(item.QuestionnaireID);
            setResearchTitle(item.Title);
        });
    }, [research]);

    // Set respondent title (deelnemer/medewerker of huishouden)
    useEffect(() => {
        setPersonaTitle(resolveRespondentTitle({ personas, adresses, personaDataRows }));
    }, [personas, adresses, personaDataRows]);

    // Sort forms from responses by date
    const sortForms = () => {
        const groupedForms = {};
    
        results && results.forEach(item => {
            const timestamp = item.Timestamp.toDate().getTime();
    
            if (!groupedForms[timestamp]) {
                groupedForms[timestamp] = {
                    date: item.Timestamp.toDate(),  // store the date
                    momentID: item.MomentID,         // store the MomentID
                    items: []                        // store the responses
                };
            }
    
            groupedForms[timestamp].items.push(item);
        });
    
        // Sort timestamps in descending order
        const sortedTimestamps = Object.keys(groupedForms)
            .map(ts => parseInt(ts))
            .sort((a, b) => b - a);
    
        // Map to the sorted form you want
        const sortedGroupedForms = sortedTimestamps.map(timestamp => {
            const group = groupedForms[timestamp];
            return {
                date: group.date,
                momentID: group.momentID,
                items: group.items
            };
        });
    
        return sortedGroupedForms;
    };
    
    useEffect(() => {
        const sortedForms = sortForms();
        setForms(sortedForms);
    }, [results]);

    // Utility to render the field title based on type
    const fieldTitleType = (item) => {
        switch(item.Type){
            case 'paragraph':
            case 'scale':
            case 'multiple-one':
            case 'number':
            case 'multiple-multiple':
                return item.Question;
            case 'matrix-one':
            case 'matrix-multiple':
                return matrixType(item);
            default:
                return item.Question;
        }
    };

    // Render matrix type fields
    const matrixType = (item) => {
        if(item.Standard === 'true'){
            return (
                <div id='meetstandaard-effect-meta-container'>
                    <Tooltip content={'Gestandaardiseerd effect'} width='80%' right='-120px' top='-15px'>
                    <p className='table-p tooltip-p-normal' id='add-inicator-meetstandaard-effect-title-container'>
                        <EffectMeta item={item.EffectId[0]}/>
                    </p>
                    </Tooltip>
                    <a href="https://meetstandaard.nl" target="_blank" rel="noopener noreferrer" className="question-standardized-sufix">(meetstandaard.nl)</a>
                </div>
            );
        } else {
            return item.Question;
        }
    };

    return (
        <div className="main">
            <div className="main-container">
                <div className="page-header">
                    <h1>Onderzoeksresultaten</h1>
                    <h2>{personaTitle}</h2>
                    <h2>{researchTitle}</h2>
                </div>
                <div className="research-results-personas-content">
                    <SelectableGrid
                        items={[
                            { id: 'forms', label: 'Formulieren' },
                            { id: 'questions', label: 'Lijst met vragen' },
                        ]}
                        getId={(item) => item.id}
                        getLabel={(item) => item.label}
                        selectedId={resultType}
                        onClick={setResultType}
                    />
                    {resultType === 'questions' &&
                        <Table
                            columns={[
                                { key: 'question', label: 'VRAAG' },
                                { key: 'responses', label: 'RESPONSES', searchable: false },
                            ]}
                            items={fields || []}
                            getItemKey={(item) => item.ID}
                            renderRow={(item) => (
                                <>
                                    <TableCell>
                                        <p>{fieldTitleType(item)}</p>
                                    </TableCell>
                                    <TableCell>
                                        <div 
                                            id='reserach-overview-responses-container' 
                                            onClick={() => { 
                                                history.push(`/${client}/researchresponsespersonas/${personaId}/${researchId}/${item.ID}`);
                                            }}
                                        >
                                            <Tooltip content='Bekijk de responses' top={'-60px'}>
                                                <ChatBubbleOutlineOutlinedIcon className='table-delete-icon' />
                                            </Tooltip>
                                            <FieldResponsesCountPersonas fieldID={item.ID} researchID={researchId} personaID={personaId} />
                                        </div>
                                    </TableCell>
                                </>
                            )}
                            searchAccessor={(item) => item?.Question || ''}
                            searchPlaceholder="Zoeken op vraag"
                            emptyMessage="Geen vragen gevonden."
                        />
                    }
                    {resultType === 'forms' &&
                        <Table
                            columns={[
                                { key: 'date', label: 'DATUM' },
                                { key: 'moment', label: 'MEETMOMENT', searchable: false },
                                { key: 'responses', label: 'RESPONSES', searchable: false },
                            ]}
                            items={forms || []}
                            getItemKey={(item) => item.date.toString()}
                            renderRow={(item) => (
                                <>
                                    <TableCell>
                                        <p>{item.date.toLocaleDateString('nl-NL', options)}</p>
                                    </TableCell>
                                    <TableCell>
                                        <MomentMeta item={item.momentID} />
                                    </TableCell>
                                    <TableCell>
                                        <div 
                                            id='reserach-overview-responses-container' 
                                            onClick={() => { 
                                                const ts = item.date;
                                                let firebaseTimestamp;
                                                if (ts.toDate) {
                                                    firebaseTimestamp = ts;
                                                } else {
                                                    firebaseTimestamp = new firebase.firestore.Timestamp(
                                                        Math.floor(ts.getTime() / 1000),
                                                        (ts.getTime() % 1000) * 1000000
                                                    );
                                                }
                                                const serializedTimestamp = `${firebaseTimestamp.seconds}-${firebaseTimestamp.nanoseconds}`;
                                                history.push(`/${client}/researchresultspersonasforms/${personaId}/${researchId}/${serializedTimestamp}`);
                                            }}
                                        >
                                            <Tooltip content='Bekijk de responses' top={'-60px'}>
                                                <ChatBubbleOutlineOutlinedIcon className='table-delete-icon' />
                                            </Tooltip>
                                            <p>({item.items.length})</p>
                                        </div>
                                    </TableCell>
                                </>
                            )}
                            searchAccessor={(item) => item?.date?.toLocaleDateString('nl-NL', options) || ''}
                            searchPlaceholder="Zoeken op datum"
                            emptyMessage="Geen formulieren gevonden."
                        />
                    }
                </div>
            </div>
        </div>
    );
};

export default ReserachResultsPersonas;
